/**
 * The two pages an emailed link lands on: confirm and unsubscribe.
 *
 * Both fire on mount with no button to press. A confirmation link that arrives
 * at a page saying "click here to confirm" has asked the reader to confirm
 * twice, and an unsubscribe link that does the same is how people give up and
 * hit "mark as spam" instead — which on a status list would take the outage
 * notices down with the maintenance ones.
 */

import React, { useEffect, useState } from 'react';
import { ds } from '../ds';
import { api, errorText } from '../api';
import { Footnote, Page, Panel } from '../components/Chrome';
import { Link, useRouter } from '../router';
import { display, monoPlain } from '../theme';

import { useT } from '../i18n';
type Phase = 'working' | 'done' | 'failed';

function Result({
  phase,
  icon,
  title,
  children
}: {
  phase: Phase;
  icon: string;
  title: string;
  children: React.ReactNode;
}) {
  const { Icon } = ds();
  const tone = phase === 'failed' ? 'var(--st-down-ink)' : 'var(--st-up-ink)';

  return (
    <Page>
      <Panel style={{ display: 'grid', gap: 8, textAlign: 'center', padding: '40px 28px' }}>
        <span style={{ color: phase === 'working' ? 'var(--text-quiet)' : tone }}>
          <Icon name={phase === 'working' ? 'loader' : icon} size={34} />
        </span>
        <h1 style={{ ...display(26), color: phase === 'working' ? undefined : tone }}>{title}</h1>
        <div style={{ display: 'grid', gap: 12, justifyItems: 'center' }}>{children}</div>
      </Panel>
    </Page>
  );
}

export function ConfirmSubscription() {
  const t = useT();
  const { query } = useRouter();
  const token = query.get('token') ?? '';
  const [phase, setPhase] = useState<Phase>('working');
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!token) {
      setError('That link is missing its token. Subscribe again from the status page.');
      setPhase('failed');
      return;
    }
    api
      .confirm(token)
      .then(() => setPhase('done'))
      .catch((err) => {
        setError(errorText(err, t('subscription.badlink.fallback')));
        setPhase('failed');
      });
  }, [token]);

  if (phase === 'working') {
    return (
      <Result phase="working" icon="loader" title={t('subscription.confirming')}>
        <Footnote>{t('subscription.moment')}</Footnote>
      </Result>
    );
  }

  if (phase === 'failed') {
    return (
      <Result phase="failed" icon="x-circle" title={t('subscription.badlink')}>
        <Footnote>{error}</Footnote>
        <Link to="/" style={{ ...monoPlain, textDecoration: 'none' }}>
          {t('subscription.back')}
        </Link>
      </Result>
    );
  }

  return (
    <Result phase="done" icon="check-circle-2" title={t('subscription.done.title')}>
      <Footnote>{t('subscription.done.body')}</Footnote>
      <Link to="/" style={{ ...monoPlain, textDecoration: 'none' }}>
        {t('subscription.back')}
      </Link>
    </Result>
  );
}

export function Unsubscribe() {
  const t = useT();
  const { query } = useRouter();
  const token = query.get('token') ?? '';
  const [phase, setPhase] = useState<Phase>('working');
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!token) {
      setError('That link is missing its token.');
      setPhase('failed');
      return;
    }
    api
      .unsubscribe(token)
      .then(() => setPhase('done'))
      .catch((err) => {
        setError(errorText(err, 'That unsubscribe link is not valid.'));
        setPhase('failed');
      });
  }, [token]);

  if (phase === 'working') {
    return (
      <Result phase="working" icon="loader" title={t('subscription.unsubscribing')}>
        <Footnote>{t('subscription.moment')}</Footnote>
      </Result>
    );
  }

  if (phase === 'failed') {
    return (
      <Result phase="failed" icon="x-circle" title={t('subscription.badlink')}>
        <Footnote>{error}</Footnote>
        <Link to="/" style={{ ...monoPlain, textDecoration: 'none' }}>
          {t('subscription.back')}
        </Link>
      </Result>
    );
  }

  return (
    <Result phase="done" icon="check-circle-2" title={t('subscription.unsubscribed.title')}>
      <Footnote>{t('subscription.unsubscribed.body')}</Footnote>
      <Link to="/" style={{ ...monoPlain, textDecoration: 'none' }}>
        {t('subscription.back')}
      </Link>
    </Result>
  );
}
